// Pin 596 (Alvin 10/16): "After adding a recipient or a guest to an itinerary, adding to calendar does not automatically
// include their email in the calendar invite event." One door for every itinerary calendar add (the .ics file and the
// Google Calendar links), so a guest list can never be dropped by one path again. Contract shared with the flows thread
// (orchestrator 10/16); keep it stable. No dependencies, so deno tests import it as is (tests/pins1016_test.ts).
//
// Nothing here sends anything: the file is METHOD:PUBLISH (importing it adds the events; the person's own calendar app
// asks before it invites anyone) and the Google link only pre-fills the guest box.

/** One event. `uid` (optional, ours): a stable id so re-importing the same stop updates it where calendars support it. */
export type InviteStop = { title: string; start: string /* ISO */; end: string /* ISO */; location?: string | null; url?: string | null; note?: string | null; uid?: string }

const EMAIL = /^[^@\s<>"',;:()[\]\\]+@[^@\s<>"',;:()[\]\\]+\.[^@\s<>"',;:()[\]\\]+$/

/** De-duplicated, lower-cased, valid emails only (empty is fine). */
export function cleanGuests(guests: (string | null | undefined)[]): string[] {
  return [...new Set(guests.map((g) => String(g ?? '').trim().toLowerCase()).filter((g) => g.length <= 254 && EMAIL.test(g)))]
}

const stamp = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
const esc = (s: string) => s.replace(/\r/g, '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')
/** A parameter value (CN=...): quoted, no quotes or line breaks inside. */
const param = (s: string) => `"${s.replace(/["\r\n]/g, '')}"`

/** RFC 5545 folding: content lines at most 75 octets, continued after CRLF + space; never splits a character. */
function fold(line: string): string {
  const enc = new TextEncoder()
  const out: string[] = []
  let cur = ''
  let bytes = 0
  for (const ch of line) {
    const n = enc.encode(ch).length
    if (bytes + n > (out.length ? 74 : 75)) { out.push(cur); cur = ''; bytes = 0 }
    cur += ch
    bytes += n
  }
  out.push(cur)
  return out.join('\r\n ')
}

/** Start and end as instants; an end at or before the start becomes start + 2 hours. Null when the start is not a date. */
function span(s: InviteStop): { start: Date; end: Date } | null {
  const a = Date.parse(s.start)
  if (Number.isNaN(a)) return null
  const b = Date.parse(s.end)
  return { start: new Date(a), end: new Date(!Number.isNaN(b) && b > a ? b : a + 2 * 3600e3) }
}

/** One Google Calendar template link for one stop, with the guests pre-filled (`add=`). */
export function googleInviteUrl(s: InviteStop, guests: string[], tz?: string): string | null {
  const t = span(s)
  if (!t) return null
  const p = new URLSearchParams({ action: 'TEMPLATE', text: s.title, dates: `${stamp(t.start)}/${stamp(t.end)}` })
  const details = [s.note ?? '', s.url && !(s.note ?? '').includes(s.url) ? `Details: ${s.url}` : ''].filter(Boolean).join('\n')
  if (details) p.set('details', details)
  if (s.location) p.set('location', s.location)
  if (tz) p.set('ctz', tz)
  const g = cleanGuests(guests)
  if (g.length) p.set('add', g.join(','))
  return `https://calendar.google.com/calendar/render?${p.toString()}`
}

/**
 * The invite for these stops: one calendar file (one VEVENT per stop, every guest as an ATTENDEE on each) and one Google
 * Calendar link per stop (a Google link carries one event) with every guest in `add=`. Stops without a valid start are
 * left out of both. `title` names the calendar file; `tz` is the zone Google shows the times in.
 */
export function buildCalendarInvite(stops: InviteStop[], opts: { guests: string[]; title?: string; tz?: string; now?: Date }): { ics: string; googleUrls: string[] } {
  const guests = cleanGuests(opts.guests)
  const now = opts.now ?? new Date()
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Magellan//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', ...(opts.title ? [`X-WR-CALNAME:${esc(opts.title)}`] : [])]
  const googleUrls: string[] = []
  stops.forEach((s, i) => {
    const t = span(s)
    if (!t) return
    const desc = [s.note ?? '', s.url && !(s.note ?? '').includes(s.url) ? `Details: ${s.url}` : ''].filter(Boolean).join('\n')
    lines.push('BEGIN:VEVENT', `UID:${s.uid ?? `${stamp(t.start)}-${i}-${s.title.replace(/[^a-z0-9]+/gi, '').slice(0, 24).toLowerCase()}`}@magellan`,
      `DTSTAMP:${stamp(now)}`, `DTSTART:${stamp(t.start)}`, `DTEND:${stamp(t.end)}`, `SUMMARY:${esc(s.title)}`,
      ...(desc ? [`DESCRIPTION:${esc(desc)}`] : []), ...(s.location ? [`LOCATION:${esc(s.location)}`] : []), ...(s.url ? [`URL:${s.url.replace(/[\r\n]/g, '')}`] : []),
      ...guests.map((g) => `ATTENDEE;CN=${param(g)};ROLE=REQ-PARTICIPANT;RSVP=TRUE:mailto:${g}`),
      'END:VEVENT')
    const g = googleInviteUrl(s, guests, opts.tz)
    if (g) googleUrls.push(g)
  })
  lines.push('END:VCALENDAR')
  return { ics: lines.map(fold).join('\r\n'), googleUrls }
}
